import React, {useState, useRef, useEffect} from "react";
import "./css/BuySell.css";
import {useRefreshContext} from "../context/RefreshContext";
import {deleteContractNote, fetchManualContractNoteNo, saveContractNote} from "@portfolio/utils/PFService";
import Charges from "../importcnote/Charges"; // Import the new CSS file
import { usePopupStack } from "../../contexts/PopupStackContext";

const BuySell = ({clientId, onClose, script,isDisplayClick,contractNoteDTO}) => {
    const today = new Date().toLocaleDateString("en-CA");  // YYYY-MM-DD format
    const formRef = useRef(null);
    const [transactions, setTransactions] = useState([]);
    const [manualContractNoteNo, setManualContractNoteNo] = useState([]);
    const {refreshPFdata} = useRefreshContext(); // 🔹 Subscribe to global refresh
    /*const [charges, setCharges] = useState([]);*/
    const { push, pop } = usePopupStack();

    useEffect(() => {
        if(isDisplayClick){
            setTransactions(contractNoteDTO.transactions);
            /*setCharges(contractNoteDTO.charges);*/
        }
    }, []);

    useEffect(() => {
        fetchManualContractNoteNo("MANUAL-CNT-");
    }, []); // Empty dependency array -> Runs only on mount

    useEffect(() => {
        const popupEntry = { onClose }; // 🔥 Use the prop directly
        push(popupEntry);
        return () => pop();
    }, []);

    const handleClose = () => {
        pop(); // 🧹 Make sure to remove this popup from the stack manually
        onClose();
    };

    const handleAddRow = async (type) => {
        if (transactions.length >= 1) {
            alert("You can only add one new transaction at a time, because one contract note can have one entry per stock. Save and then add another transaction separately");
            return;
        }
        const newRow = {
            script: script,
            transactionType: type,
            quantity: 0,
            weightedAvgPrice: 0,
            brokerage: 0,
            total: 0.00
        };
        setTransactions([newRow]);
    };

    const handleRemoveRow = (index) => {
        setTransactions((prevTransactions) =>
            prevTransactions.filter((_, idx) => idx !== index)
        );
    };

    const handleInputChange = (index, field, value) => {
        setTransactions((prevTransactions) =>
            prevTransactions.map((txn, idx) =>
                idx === index
                    ? {
                        ...txn,
                        [field]: value,
                        transactionType: field === "transactionType" ? value : txn.transactionType, // Ensure transactionType is updated
                        totalValue:
                            ((field === "quantity" ? value : parseFloat(txn.quantity) || 0) *
                                (field === "weightedAvgPrice" ? value : parseFloat(txn.weightedAvgPrice) || 0)) +
                            (field === "brokerage" ? value : parseFloat(txn.brokerage) || 0),

                    }
                    : txn
            )
        );
    };

    const handleSave = async () => {
        let manualNote = contractNoteDTO?.contractNote?.contractNoteNo || "";
        if(!isDisplayClick) {
            manualNote = await fetchManualContractNoteNo("MANUAL-CNT-"); // ✅ Now we get the actual value
            //console.log("📌 manualContractNoteNo BEFORE using:", manualNote); // ❌ Logs empty array []
        }
        const formData = new FormData(formRef.current);

        // Extracting required fields
        const broker = formData.get("broker")?.trim();
        const totalCharges = formData.get("total")?.trim();

        // Validate required fields
        if (!broker) {
            alert("Broker is required.");
            return;
        }

        // Validate Buy and Sell Transactions
        const isValidTransaction = (transactions) => {
            return transactions.every((_, idx) => {
                const script = formData.get(`script-${idx}`)?.trim();
                const transactionType = formData.get(`transactionType-${idx}`)?.trim();
                const quantity = formData.get(`quantity-${idx}`);
                const weightedAvgPrice = formData.get(`price-${idx}`);
                const brokerage = formData.get(`brokerage-${idx}`);
                const total = formData.get(`total-${idx}`);

                if (!script) {
                    alert(`Transaction: Script is required.`);
                    return false;
                }
                if (!quantity || isNaN(quantity)) {
                    alert(`Transaction: Quantity must be a number.`);
                    return false;
                }
                if (!weightedAvgPrice || isNaN(weightedAvgPrice)) {
                    alert(`Transaction: Purchase Price must be a number.`);
                    return false;
                }
                if (!brokerage || isNaN(brokerage)) {
                    alert(`Transaction: Brokerage must be a number.`);
                    return false;
                }
                if (!total || isNaN(total)) {
                    alert(`Transaction: Total Amount must be a number.`);
                    return false;
                }
                return true;
            });
        };

        if (!isValidTransaction(transactions)) {
            return;
        }

        // Construct ContractNoteDTO
        const newContractNote = {
            contractNote: {
                contractNoteNo: manualNote,
                clientId: clientId,
                brokerName: broker,
                tradeDate: formData.get("date"),
                cnoteType: "CNOTE",
            },
            cnotesCharge: {
                contractNoteNo: manualNote,
                securitiesTransactionTaxEquity: 0.0,
                stampDutyNetTotal: 0.0,
                gstTotal: 0.0,
                payInPayOutObligationNetTotal: 0.0,
                otherCharges: 0.0,
                netAmountNetTotal: 0.0,
            },
            transactions: transactions.map((txn, idx) => { // ✅ Include idx here
                const transactionType = formData.get(`transactionType-${idx}`);
                const quantity = parseInt(formData.get(`quantity-${idx}`)) || 0;
                const totalValue = parseFloat(formData.get(`total-${idx}`)) || 0.0;
                return {
                    type: "StockTransactionDTO",
                    contractNoteNo: manualNote,
                    clientId: clientId,
                    symbol: formData.get(`script-${idx}`),
                    transactionType: transactionType,
                    quantity: quantity,
                    weightedAvgPrice: parseFloat(formData.get(`price-${idx}`)) || 0.0,
                    brokerage: parseFloat(formData.get(`brokerage-${idx}`)) || 0.0,
                    totalValue: totalValue,
                    netQuantity: transactionType === "Sell" ? -quantity : quantity, // Negative for Sell, Positive for Buy
                    netObligation: transactionType === "Sell" ? totalValue : -totalValue, // Positive for Sell, Negative for Buy
                    tradeDate: formData.get("date")
                };
            }),
            cnoteType: "CNOTE"
        };
        // Save contract note
        const result = await saveContractNote(newContractNote, isDisplayClick); // Wait for save to complete
        if (result.success) {
            refreshPFdata(); // ✅ Now refreshes AFTER the save is successful
            onClose(); // Close the popup after successful Save
        }
    };

    const handleDelete = async () => {
        try {
            const result =await deleteContractNote(contractNoteDTO.contractNote.contractNoteNo); // Wait for save to complete
            if (result.success) {
                refreshPFdata(); // ✅ Now refreshes AFTER the save is successful
                onClose(); // Close the popup after successful Save
            }
        } catch (error) {
            console.error("Error deleting:", error);
        }
    };

    return (
        <div className="buysell-overlay">
            <form ref={formRef}>
                <div className="buysell-popup" onClick={(e) => e.stopPropagation()}>

                    <div className="header-row">
                        <h3>Buy/Sell</h3>
                        <h3>{clientId}</h3>
                    </div>
                    <div className="header-field-container">
                        <div className="header-row-second">
                            <div className="form-group half-width">
                                <label className="general-label">Date:</label>
                                <input type="date" name="date" defaultValue={today}/>
                            </div>
                            <div className="form-group full-width">
                                <label className="general-label">Client ID:</label>
                                <input type="text" value={clientId} readOnly/>
                            </div>
                        </div>
                        <div className="header-row-second">
                            <div className="form-group half-width">
                                <label className="general-label">Broker:</label>
                                <input type="text" name="broker"/>
                            </div>
                            <div className="form-group full-width">
                                <label className="general-label">Contract Note No:</label>
                                <input type="text" name="contractNoteNo" value={manualContractNoteNo}
                                       title="This value will be appended with a random number if it already exists in the database"/>
                            </div>
                        </div>
                    </div>


                    <div className="transaction-container">
                        <div className="transaction-header">
                            <h5>Transactions</h5>
                            <div className="small-buttons">
                                <button type="button" className="new-button" onClick={() => handleAddRow("buy")}>New
                                </button>
                            </div>
                        </div>
                        <table className="contract-table">
                            <thead>
                            <tr>
                                <th>Script</th>
                                <th>Type</th>
                                <th>Quantity</th>
                                <th>Pur. Price</th>
                                <th>Brokerage</th>
                                <th>Total Amount</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {transactions.map((txn, idx) => (
                                <tr key={idx}>
                                    <td><input name={`script-${idx}`} className="transaction-input"
                                               value={txn.script} required/></td>
                                    <td><select name={`transactionType-${idx}`}
                                                className="transaction-input-select"
                                                value={txn.transactionType}
                                                onChange={(e) => handleInputChange(idx, "transactionType", e.target.value)}>
                                        <option value="Buy">Buy</option>
                                        <option value="Sell">Sell</option>
                                    </select>
                                    </td>
                                    <td><input name={`quantity-${idx}`} className="transaction-input"
                                               value={txn.quantity} required
                                               onChange={(e) => {
                                                   if (!txn.transactionType) {
                                                       alert("Please select Buy or Sell before editing.");
                                                       return;
                                                   }
                                                   handleInputChange(idx, "quantity", parseFloat(e.target.value) || 0)
                                               }}/></td>
                                    <td><input name={`price-${idx}`} className="transaction-input"
                                               value={txn.weightedAvgPrice}
                                               required
                                               onChange={(e) => {
                                                   if (!txn.transactionType) {
                                                       alert("Please select Buy or Sell before editing.");
                                                       return;
                                                   }
                                                   handleInputChange(idx, "weightedAvgPrice", parseFloat(e.target.value) || 0)
                                               }}/></td>
                                    <td><input name={`brokerage-${idx}`} className="transaction-input"
                                               value={txn.brokerage} required
                                               onChange={(e) => {
                                                   if (!txn.transactionType) {
                                                       alert("Please select Buy or Sell before editing.");
                                                       return;
                                                   }
                                                   handleInputChange(idx, "brokerage", parseFloat(e.target.value) || 0)
                                               }}/></td>
                                    <td>
                                        <input
                                            name={`total-${idx}`}
                                            className="transaction-input"
                                            value={parseFloat(txn.totalValue).toFixed(2)}
                                            readOnly
                                        />
                                    </td>
                                    <td>
                                        <button type="button" className="cross-button"
                                                onClick={() => handleRemoveRow(idx)}>❌
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Charges Section */}
                    {/*<div>
                        <div className="importcnote-popup-content">
                            <Charges initialCharges={charges} onChargesUpdate={setCharges}/>
                        </div>
                    </div>*/}
                    <div className="contract-buttons">
                        {isDisplayClick && (
                            <button type="button" className="delete-button" onClick={handleDelete}>
                                Delete CNote
                            </button>
                        )}
                        <div className="right-buttons">
                            <button type="button" className="save-button" onClick={handleSave}>Save</button>
                            <button type="button" className="cancel-button" onClick={handleClose}>Cancel</button>
                        </div>
                    </div>
                </div>

            </form>
</div>
)
    ;
};

export default BuySell;
